/**
 * 首页
 * ============================================================
 * 对应需求文档 V1.0 的 B01 / 交互设计 4.1：
 *   · 顶部显示当前学习方言、目标、进度
 *   · 主卡片「继续跟 AI 老师学习」，保留上次位置
 *   · 复习区优先展示待复习词句
 *   · 推荐内容要解释推荐原因
 *
 * ── 2026-10 布局重做（用户反馈"看起来太凌乱"）──────────────
 * 查下来"凌乱"不是配色问题，是**信息架构在跟底部导航抢**：
 *
 *   1. 快捷入口 5 个里，有 2 个（全部课程 / 场景对话）与底部导航
 *      完全重复 —— 用户在首页看到"全部课程"，点进去发现就是底部的"学习"。
 *   2. 5 个格子排 2 列 → 最后一行只剩 1 个，右侧留白，
 *      这是视觉上最直接的"不齐"。
 *   3. "复习"出现两次（快捷入口 + 上方复习区）。
 *   4. 复习区用 3 张同样大小的白卡展示纯文本，抢走了主行动的注意力。
 *   5. "当前方言能力"是一组内部指标标签，放在首屏等于把体检报告贴门口。
 *
 * 现在：**首页只回答一个问题 —— 我现在该做什么。**
 *   · 一个主行动（继续学习），视觉重量明显高于其它
 *   · 复习压成一行（有才显示）
 *   · 只放底部导航里没有的入口，且只放两个
 *   · 方言能力折叠成一行，点开才看详情（"如实告知"保留，但不占首屏）
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { getSelectedDialectId, fetchDialect, STATUS_STYLE } from '../lib/dialects';
import { getProgress, getCompletedLessons, findNextLesson, getWeeklyStats } from '../lib/courseProgress';
import { getReviewStats } from '../lib/reviewQueue';
import BottomNav from '../components/BottomNav';
import SiteFooter from '../components/SiteFooter';

export default function HomePage() {
  const navigate = useNavigate();

  const [dialect, setDialect] = useState(null);
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [reviewStats, setReviewStats] = useState(() => getReviewStats());
  /** 方言能力默认收起：首屏只留"该做什么"，细节点开再看 */
  const [capsOpen, setCapsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const id = getSelectedDialectId();
    try {
      const [cur, d] = await Promise.all([
        api.get('/api/curriculum', { params: { dialect: id } }),
        fetchDialect(id).catch(() => null),
      ]);
      const data = cur.data?.data || cur.data;
      setSummary(data.summary);
      setGroups(data.groups || []);
      setGroups((g) => [...g, ...(data.generatedGroups || [])]);
      if (d) setDialect(d);
    } catch (e) {
      console.error('[首页] 加载失败:', e);
    } finally {
      setLoading(false);
    }
    setReviewStats(getReviewStats());
  }, []);

  useEffect(() => {
    load();
    // 从别处返回（切换方言、上完课）时刷新
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const completed = getCompletedLessons();
  const nextLesson = findNextLesson(groups);
  const progress = getProgress(summary?.totalLessons || 0);
  const pct = progress?.percent ?? 0;
  const caps = dialect?.badges || [];
  // 每次渲染都从 localStorage 现算：从课上回来时 load() 会触发重渲染，周报自然跟着更新
  const weekly = getWeeklyStats(7);

  return (
    <div className="min-h-screen bg-beige-light pb-28 dark:bg-zinc-900">
      {/*
        顶部不再用整块高饱和渐变。
        渐变本身没问题，但它是页面上最大的一块颜色，而它承载的信息
        （方言名 + 进度）只需要两行 —— 视觉重量和内容量严重不匹配，
        下面每张白卡都被它压住。现在改成"米色底上的深色大字"，
        把颜色留给真正需要强调的东西（主行动）。
      */}
      <header className="px-5 pb-5 pt-10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold tracking-wide text-zinc-400">正在学</p>
            <h1 className="mt-0.5 truncate text-[26px] font-bold leading-tight text-zinc-900 dark:text-zinc-50">
              {dialect?.displayName || '加载中…'}
            </h1>
          </div>
          <Link
            to="/dialects"
            className="mt-1 flex shrink-0 items-center gap-1 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-bold text-zinc-600 active:scale-95 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
          >
            <span className="material-icons !text-[14px]">translate</span>
            换方言
          </Link>
        </div>

        {summary && (
          <div className="mt-4 flex items-center gap-3">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-200/80 dark:bg-zinc-700">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="shrink-0 text-[11px] font-bold tabular-nums text-zinc-500 dark:text-zinc-400">
              {completed.length} / {summary.totalLessons} 课
            </span>
          </div>
        )}
      </header>

      <main className="space-y-3 px-5">
        {/* ── ① 主行动：继续学习 ──────────────────────────────
            整页只有这一张"大卡"。其它都是它的补充，不能和它一样重。 */}
        <section>
          {/* 小标题保留：「接着第 4 课」说的是进度，但没说这一块是干嘛的。
              一行 overline 的成本很低，却让首屏有一个明确的落点。 */}
          <h2 className="mb-2 px-1 text-[12px] font-bold text-zinc-500 dark:text-zinc-400">
            继续学习
          </h2>
          {loading ? (
            <div className="h-[104px] animate-pulse rounded-2xl bg-white dark:bg-zinc-800" />
          ) : nextLesson ? (
            <button
              type="button"
              onClick={() => navigate(`/session/${nextLesson.id}`)}
              className="flex w-full items-center gap-4 rounded-2xl bg-white p-4 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)] active:scale-[0.99] dark:bg-zinc-800"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-2xl">
                {nextLesson.icon || '📘'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="mb-1 block text-[11px] font-bold text-primary">
                  {completed.length === 0 ? '从这里开始' : `接着第 ${completed.length + 1} 课`}
                </span>
                <span className="block truncate text-[15px] font-bold text-zinc-900 dark:text-zinc-50">
                  {nextLesson.title}
                </span>
                <span className="mt-0.5 block truncate text-[12px] text-zinc-500 dark:text-zinc-400">
                  {nextLesson.goal}
                </span>
              </span>
              <span className="material-icons shrink-0 text-[32px] text-primary">play_circle</span>
            </button>
          ) : (
            <div className="rounded-2xl bg-white p-5 text-center dark:bg-zinc-800">
              <p className="text-sm text-zinc-500 dark:text-zinc-400">这个方言还没有课程</p>
              <Link
                to="/generate"
                className="mt-3 inline-block rounded-full bg-primary px-4 py-2 text-xs font-bold text-white"
              >
                生成一课
              </Link>
            </div>
          )}
        </section>

        {/* ── ② 复习：压成一行 ────────────────────────────────
            原来这里是 3 张等大的白卡，每张一段文字。
            但"该复习了"本身只是**一个提醒**，不需要三条预览 ——
            用户点进去自然能看到全部。压成一行后，主卡的独特性才成立。 */}
        {reviewStats?.total > 0 && (
          <Link
            to="/review"
            className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 active:scale-[0.99] dark:bg-zinc-800"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-900/30">
              <span className="material-icons !text-[16px] text-amber-600 dark:text-amber-400">refresh</span>
            </span>
            <span className="flex-1 text-[13px] text-zinc-700 dark:text-zinc-200">
              <b className="font-bold">{reviewStats.total}</b> 项待复习
            </span>
            <span className="text-[12px] font-bold text-primary">去复习</span>
            <span className="material-icons !text-[16px] text-zinc-300">chevron_right</span>
          </Link>
        )}

        {/* ── ③ 只放底部导航里没有的入口 ──────────────────────
            原来 5 个格子里"全部课程"= 底部「学习」、「场景对话」= 底部「对话」。
            那不是快捷入口，是同一批按钮的第二份。现在只留两个真·入口。 */}
        <section className="grid grid-cols-2 gap-3">
          <MiniEntry to="/generate" icon="auto_awesome" label="生成一课" desc="按你的场景定制" />
          <MiniEntry to="/dialects" icon="explore" label="方言目录" desc="看看还能学什么" />
        </section>

        {/* ── ④ 最近 7 天小结 ────────────────────────────────
            放在动作入口之后：它是"回顾"，不是"该做什么"，
            不该和主卡抢首屏。同时把下半屏那片空白填得有内容而不是塞满。

            ⚠️ 用「最近 7 天」而不是「本周（周一至周日）」：
               按自然周算，周一早上打开永远是 0，用户刚学了却看到一片零，
               会觉得"没记上"。滚动 7 天任何一天打开都是真实进展。 */}
        <WeeklyCard stats={weekly} />

        {/* ── ⑤ 方言能力：默认折叠 ────────────────────────────
            这是"如实告知"（需求文档要求表明哪个方言能听/能说），
            但它是一组内部指标，不该占首屏。收成一行，谁在乎谁点开。 */}
        {dialect && caps.length > 0 && (
          <section className="overflow-hidden rounded-2xl bg-white/70 dark:bg-zinc-800/70">
            <button
              type="button"
              onClick={() => setCapsOpen((v) => !v)}
              className="flex w-full items-center gap-2 px-4 py-3 text-left"
            >
              <span className="text-[12px] font-bold text-zinc-500 dark:text-zinc-400">
                当前方言能力
              </span>
              <span className="flex-1" />
              <span className="text-[11px] text-zinc-400">{caps.length} 项</span>
              <span
                className={`material-icons !text-[18px] text-zinc-300 transition-transform ${
                  capsOpen ? 'rotate-180' : ''
                }`}
              >
                expand_more
              </span>
            </button>
            {capsOpen && (
              <div className="border-t border-zinc-100 px-4 py-3 dark:border-zinc-700">
                <div className="space-y-1.5">
                  {caps.map((b) => {
                    const st = STATUS_STYLE[b.id] || STATUS_STYLE.unavailable;
                    return (
                      <div key={b.key} className="flex items-center justify-between">
                        <span className="text-xs text-zinc-600 dark:text-zinc-300">{b.label}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>
                          {st.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {dialect.review?.verdict && dialect.review.verdict !== 'unreviewed' && (
                  <p className="mt-2 border-t border-zinc-100 pt-2 text-[10px] leading-relaxed text-zinc-400 dark:border-zinc-700">
                    试用状态：{dialect.review.verdictLabel}
                    {dialect.review.reason ? ` · ${dialect.review.reason}` : ''}
                  </p>
                )}
              </div>
            )}
          </section>
        )}
      </main>

      {/* 备案号必须出现在首页底部（工信部要求，且要能跳 beian.miit.gov.cn） */}
      <SiteFooter />

      <BottomNav />
    </div>
  );
}

/** 次级入口（一行两个）：比原来的方块更矮，避免和主卡抢高度 */
function MiniEntry({ to, icon, label, desc }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 rounded-2xl bg-white px-3.5 py-3 active:scale-[0.98] dark:bg-zinc-800"
    >
      <span className="material-icons !text-[18px] text-primary">{icon}</span>
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-bold text-zinc-800 dark:text-zinc-100">
          {label}
        </span>
        <span className="block truncate text-[10px] text-zinc-400">{desc}</span>
      </span>
    </Link>
  );
}

/**
 * 最近 7 天小结
 *
 * 两个数字 + 一排柱状。柱状不是装饰：它能立刻回答"我是集中学了一天，
 * 还是每天匀一点"—— 这是纯数字回答不了、但学习者最关心的问题。
 *
 * 空状态不说"0 课 0 个词"：那读起来像"你这周什么都没做"。
 * 换成一句邀请，指向上面那节课。
 */
function WeeklyCard({ stats }) {
  const { lessons, vocab, phrases, activeDays, byDay } = stats;
  const units = (vocab || 0) + (phrases || 0);
  const max = Math.max(1, ...byDay);
  const empty = !lessons;

  return (
    <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
      <div className="flex items-baseline justify-between">
        <h2 className="text-[12px] font-bold text-zinc-500 dark:text-zinc-400">最近 7 天</h2>
        {!empty && (
          <span className="text-[11px] text-zinc-400">{activeDays} 天有学习</span>
        )}
      </div>

      {empty ? (
        <p className="mt-2 text-[12px] leading-relaxed text-zinc-400 dark:text-zinc-500">
          这几天还没开课。上面那节十分钟就能学完，学完这里会有记录。
        </p>
      ) : (
        <div className="mt-2.5 flex items-end justify-between gap-3">
          <div className="flex items-end gap-6">
            <div>
              <div className="flex items-baseline">
                <span className="text-[24px] font-bold leading-none tabular-nums text-zinc-900 dark:text-zinc-50">
                  {lessons}
                </span>
                <span className="ml-1 text-[11px] text-zinc-400">课</span>
              </div>
              <p className="mt-1 text-[10px] text-zinc-400">学完</p>
            </div>
            {/* 老课程没有词数记录时，宁可只显示课数，也不显示"0 个词句" */}
            {units > 0 && (
              <div>
                <div className="flex items-baseline">
                  <span className="text-[24px] font-bold leading-none tabular-nums text-zinc-900 dark:text-zinc-50">
                    {units}
                  </span>
                  <span className="ml-1 text-[11px] text-zinc-400">词句</span>
                </div>
                <p className="mt-1 text-[10px] text-zinc-400">学到</p>
              </div>
            )}
          </div>

          <div className="flex h-10 shrink-0 items-end gap-1">
            {byDay.map((v, i) => (
              <div
                key={i}
                title={`${v} 课`}
                className={`w-2.5 rounded-sm ${
                  v > 0 ? 'bg-primary' : 'bg-zinc-100 dark:bg-zinc-700'
                }`}
                style={{ height: `${Math.max(4, (v / max) * 40)}px` }}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
